import type {
  ArchiveUserCardResponse,
  GetInvoiceResponse,
  ApiClient
} from '@unitedtraders/dizraptor-gateway-api-sdk';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { fromPromise } from 'mobx-utils';
import type { IPromiseBasedObservable } from 'mobx-utils';
import { object, identifier, date, alias, custom } from 'serializr';
import { iocNames } from '../iocNames';
import { createModelSchema } from '../services/EntityCache';
import type { IMoney } from '../types/IMoney';
import { Idea, IIdea } from './Idea';
import { Money } from './Money';

export interface IUserCardOrderAgreementRequired extends IUserCard {
  type: 'UserCardOrderAgreementRequired';
  alreadyDepositedAmount: IMoney;
  agreementLink?: string;
}

export interface IUserCardBuyOrderAmountRequired extends IUserCard {
  type: 'UserCardBuyOrderAmountRequired';
}

export interface IUserCardOrderFundsReceived extends IUserCard {
  type: 'UserCardOrderFundsReceived';
  alreadyDepositedAmount: IMoney;
  overpayment: IMoney;
  hasOverpayment: boolean;
}

export interface IUserCardOrderFundsRequired extends IUserCard {
  type: 'UserCardOrderFundsRequired';
  alreadyDepositedAmount: IMoney;
  invoiceUrlResult?: IPromiseBasedObservable<GetInvoiceResponse>;
  getInvoiceUrl: () => void;
}

export interface IUserCardOrderOpen extends IUserCard {
  type: 'UserCardOrderOpen';
}

export interface IUserCardPositionClosed extends IUserCard {
  type: 'UserCardPositionClosed';
  withdrawAmount: IMoney;
  actualPnl: IMoney;
  actualPnlPercent: number;
  isArchived: boolean;
  archiveResult?: IPromiseBasedObservable<ArchiveUserCardResponse>;
  archiveUserCard: () => void;
}

export interface IUserCardPositionOpen extends IUserCard {
  type: 'UserCardPositionOpen';
  actualPnl: IMoney;
  actualPnlPercent: number;
  positionValue: IMoney;
  positionQty: number;
}

export interface IUserCardRejected extends IUserCard {
  type: 'UserCardRejected';
  rejectDate: Date;
  rejectReason: string;
  isArchived: boolean;
  archiveResult?: IPromiseBasedObservable<ArchiveUserCardResponse>;
  archiveUserCard: () => void;
}

export type AnyUserCard =
  | IUserCardOrderAgreementRequired
  | IUserCardOrderFundsReceived
  | IUserCardOrderFundsRequired
  | IUserCardOrderOpen
  | IUserCardPositionClosed
  | IUserCardPositionOpen
  | IUserCardRejected
  | IUserCardBuyOrderAmountRequired;

export type CardType = AnyUserCard['type'];

export interface IUserCard {
  id: number;
  idea: IIdea;
  creationDate: Date;
  orderAmount: IMoney;
  canChangeOrderAmount: boolean;
}

export const CACHE_KEY = 'UserCard';

export class UserCard implements IUserCard {
  @observable
  type!: string;

  id!: number;

  idea!: Idea;

  creationDate!: Date;

  @observable
  orderAmount!: IMoney;

  canChangeOrderAmount!: boolean;

  alreadyDepositedAmount!: IMoney;

  agreementLink?: string;

  @observable
  invoiceUrlResult?: IPromiseBasedObservable<GetInvoiceResponse>;

  withdrawAmount!: IMoney;

  actualPnl!: IMoney;

  actualPnlPercent!: number;

  @observable
  archiveResult?: IPromiseBasedObservable<ArchiveUserCardResponse>;

  @observable
  isArchived!: boolean;

  positionValue!: IMoney;

  positionQty!: number;

  rejectDate!: Date;

  rejectReason!: string;

  constructor(private apiClient: ApiClient) {
    makeObservable(this);
  }

  @action.bound
  getInvoiceUrl() {
    this.invoiceUrlResult = fromPromise(
      this.apiClient.invoiceService
        .getInvoice({ userCardId: +this.id }, undefined, {
          transformError: true
        })
        .then(({ data }) => data)
    );
  }

  @action.bound
  archiveUserCard() {
    this.archiveResult = fromPromise(
      this.apiClient.portfolioService.archiveUserCard({ userCardId: +this.id }, undefined, {
        transformError: true
      })
    );

    this.archiveResult.then(
      () => {
        runInAction(() => {
          this.isArchived = true;
        });
      },
      () => {}
    );
  }

  @computed
  get overpayment(): IMoney {
    return {
      value: this.alreadyDepositedAmount.value - this.orderAmount.value,
      currency: { code: this.alreadyDepositedAmount.currency.code }
    };
  }

  @computed
  get hasOverpayment() {
    return this.overpayment.value > 0;
  }
}

export const userCardSchema = createModelSchema(
  UserCard,
  {
    id: identifier(),
    type: alias(
      '__typename',
      custom(String, (typename: string) => typename.replace('Dizraptor', ''))
    ),
    idea: object(Idea),
    creationDate: date(),
    orderAmount: object(Money),
    canChangeOrderAmount: true,
    alreadyDepositedAmount: object(Money),
    agreementLink: true,
    isArchived: true,
    withdrawAmount: object(Money),
    actualPnl: object(Money),
    actualPnlPercent: true,
    positionValue: object(Money),
    positionQty: true,
    rejectDate: date(),
    rejectReason: true
  },
  context => {
    const apiClient = context.args.inject<ApiClient>(iocNames.dizraptorApi);
    return new UserCard(apiClient);
  }
);
